import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Building2, Eye, EyeOff } from 'lucide-react';

const inputCls = "w-full bg-white border border-[#E2DDD3] rounded-lg px-4 py-3 text-[14px] text-[#1C1C1C] font-sans placeholder:text-[#D9D3C8] focus:border-[#1F3A34] focus:ring-1 focus:ring-[#1F3A34]/20 focus:outline-none transition-all duration-200";

export default function Landing() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Trim whitespace that autofill might add
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    
    if (!cleanEmail || !cleanPassword) {
      setError('Please fill in email and password.');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister
        ? { name: name.trim(), email: cleanEmail, password: cleanPassword }
        : { email: cleanEmail, password: cleanPassword };
      const { data } = await api.post(endpoint, payload);
      login(data, data.session_token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong.';
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EB] flex">
      {/* Left brand */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#1F3A34] flex-col justify-center px-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white/90" strokeWidth={1.8} />
            </div>
            <span className="font-serif text-2xl font-semibold text-white tracking-tight">EstaFlow</span>
          </div>
          <h1 className="font-serif text-[44px] text-white font-medium leading-[1.12] mb-6">
            Capital intelligence<br />for real estate
          </h1>
          <p className="text-white/50 font-sans text-[15px] leading-relaxed max-w-sm">
            Rendement, cashflow, leverage & scenario-analyse. Gebouwd voor professionele vastgoedinvesteerders.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-20">
        <div className="max-w-[360px] w-full mx-auto">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-8 h-8 rounded-lg bg-[#1F3A34] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-serif text-xl font-semibold text-[#1C1C1C]">EstaFlow</span>
          </div>

          <h2 className="font-serif text-[32px] text-[#1C1C1C] font-medium leading-tight mb-1.5">
            {isRegister ? 'Create account' : 'Welcome back'}
          </h2>
          <p className="text-[14px] text-[#8C877F] font-sans mb-8">
            {isRegister ? 'Start analysing your portfolio.' : 'Sign in to your portfolio.'}
          </p>

          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-5">
            {isRegister && (
              <div>
                <label className="text-[12px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Full name</label>
                <input data-testid="auth-name" type="text" autoComplete="new-name" value={name} onChange={e => setName(e.target.value)} required className={inputCls} placeholder="Your name" />
              </div>
            )}
            <div>
              <label className="text-[12px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Email</label>
              <input data-testid="auth-email" type="email" autoComplete="new-email" value={email} onChange={e => setEmail(e.target.value)} required className={inputCls} placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-[12px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Password</label>
              <div className="relative">
                <input data-testid="auth-password" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required className={inputCls + ' pr-10'} placeholder="Min. 6 characters" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C877F] hover:text-[#5A5A5A]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && <p data-testid="auth-error" className="text-[13px] text-[#8A3C3C] font-sans">{error}</p>}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[14px] py-3.5 font-sans font-medium transition-colors duration-200 disabled:opacity-50">
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </button>

            <p className="text-center text-[13px] text-[#8C877F] font-sans pt-1">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button data-testid="auth-toggle-btn" type="button"
                onClick={() => { setIsRegister(!isRegister); setError(''); }}
                className="text-[#1F3A34] font-medium hover:underline">
                {isRegister ? 'Sign In' : 'Create Account'}
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
